"use client";

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { X, CaretLeft } from 'phosphor-react';
import { useDispatch, useSelector } from 'react-redux';
import { UpdateSidebarType, SetCurrentPatientDataForChat } from '../redux/slices/app';
import axios from 'axios';

import PatientSearchForm from '../sections/main/PatientSearchForm';
import PatientSearchResults from './PatientSearchResults';

const PatientInformation = ({ onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { chatParams, selectedChatId } = useSelector((state) => state.app);

  const [patientData, setPatientData] = useState(null);
  const [loadingPatientData, setLoadingPatientData] = useState(false);
  const [patientError, setPatientError] = useState(null);

  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const currentUser = useMemo(() => {
    const storedUser = sessionStorage.getItem('currentUser');
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const fetchPatientRecord = useCallback(async () => {
    if (!chatParams || !selectedChatId || !currentUser || !currentUser.chatToken) {
      setPatientError('Missing chat context or user token to fetch patient record.');
      setPatientData(null);
      return;
    }

    setLoadingPatientData(true);
    setPatientError(null);

    try {
      const response = await axios.post(
        'https://backend-stgweb.meftii.com/service/getPatientRecord',
        {
          userId: chatParams.senderId,
          // chatId: selectedChatId,
            chatId: splitAndRemoveDuplicates(selectedChatId, 24)
        },
        {
          headers: {
            Authorization: currentUser.chatToken,
          },
        }
      );

      if (response.data?.status === 'true' && response.data?.data) {
        setPatientData(response.data.data);
        setPatientError(null);
      } else {
        setPatientData(null);
        if (
          response.data?.message &&
          response.data.message !== 'No patient record found for this chat.'
        ) {
          setPatientError('No patient record found for this chat.');
        } else {
          setPatientError(null);
        }
      }
    } catch (error) {
      console.error('Error fetching patient record:', error);
      setPatientData(null);
      setPatientError(
        error.response?.data?.message ||
          'An unexpected error occurred while fetching patient data.'
      );
    } finally {
      setLoadingPatientData(false);
    }
  }, [chatParams, selectedChatId, currentUser]);

  useEffect(() => {
    if (!showPatientSearch) fetchPatientRecord();
  }, [chatParams, selectedChatId, currentUser, showPatientSearch, fetchPatientRecord]);

  const formatDob = (dobString) => {
    if (!dobString || dobString.length !== 8) return 'N/A';
    const year = dobString.substring(0, 4);
    const month = parseInt(dobString.substring(4, 6), 10) - 1;
    const day = dobString.substring(6, 8);
    const date = new Date(year, month, day);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Helper
  function splitAndRemoveDuplicates(str, chunkSize) {
  const chunks = [];

  // Split the string
  for (let i = 0; i < str.length; i += chunkSize) {
    chunks.push(str.slice(i, i + chunkSize));
  }

  // Remove duplicate chunks
  const uniqueChunks = [...new Set(chunks)];

  // Join back into a string
  return uniqueChunks.join("");
}



  const handlePatientSelected = async (patient) => {
    try {
      const storedUser = sessionStorage.getItem('currentUser');
      if (!storedUser) {
        setPatientError('User not logged in. Please log in again.');
        return;
      }
      const currentUser = JSON.parse(storedUser);
      const chatToken = currentUser.chatToken;
      const orgId = currentUser.organizationList?.[0]?.orgId;
      const addedBy = currentUser.userId || currentUser.id;

      if (!chatToken || !orgId || !addedBy || !chatParams || !selectedChatId) {
        setPatientError('Missing user or chat context information.');
        return;
      }

      const payload = {
        patientId: patient.pid,
        chatId: splitAndRemoveDuplicates(selectedChatId, 24),
        chat_type: chatParams.chat_type,
        orgId,
        addedBy,
      };

      const response = await axios.post('https://backend-stgweb.meftii.com/addPatientRecord', payload, {
        headers: {
          Authorization: chatToken,
        },
      });

      if (response.data?.status === 'true') {
        setPatientData(patient);
        setShowPatientSearch(false); // Hide search UI
        setSearchResults([]); // Clear search results
        setPatientError(null); // Clear any patient errors
      } else {
        setPatientError(response.data?.message || 'Failed to associate patient with chat.');
      }
    } catch (error) {
      console.error('Error associating patient:', error);
      setPatientError(
        error.response?.data?.message ||
          'Failed to associate patient. Please try again.'
      );
    }
  };

  const handleCancelSearch = () => {
    setShowPatientSearch(false);
    setSearchResults([]);
    setPatientError(null);
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Stack sx={{ height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            boxShadow: '0px 0px 2px rgba(0.25)',
            width: '100%',
            backgroundColor:
              theme.palette.mode === 'light' ? '#F8FAFF' : theme.palette.background,
          }}
        >
          <Stack
            sx={{ height: '100%', p: 2 }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={3}
          >
            {showPatientSearch ? (
              <IconButton onClick={handleCancelSearch}>
                <CaretLeft size={24} color={theme.palette.text.primary} />
              </IconButton>
            ) : null}

            <Typography variant="subtitle2">Patient Information</Typography>

            <IconButton
              onClick={() => {
                onClose();
                dispatch(UpdateSidebarType('CONTACT'));
              }}
            >
              <X />
            </IconButton>
          </Stack>
        </Box>

        {/* Body */}
        <Stack
          sx={{ height: '100%', position: 'relative', flexGrow: 1, overflowY: 'auto' }}
          p={3}
          spacing={2}
        >
          {showPatientSearch ? (
            <>
              <PatientSearchForm
                onSearchComplete={setSearchResults}
                onCancelSearch={handleCancelSearch}
              />

              {searchResults.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Search Results:
                  </Typography>
                  <PatientSearchResults
                    patients={searchResults}
                    onSelectPatient={handlePatientSelected}
                  />
                </Stack>
              )}
            </>
          ) : loadingPatientData ? (
            <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading patient data...
              </Typography>
            </Stack>
          ) : patientError ? (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flexGrow: 1 }}>
              <Alert severity="info">{patientError}</Alert>

              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 3, width: '100%' }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    onClose();
                    dispatch(UpdateSidebarType('CONTACT'));
                  }}
                >
                  Cancel
                </Button>

                <Button variant="contained" onClick={() => setShowPatientSearch(true)}>
                  Add Patient
                </Button>
              </Stack>
            </Stack>
          ) : patientData ? (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                Patient Details
              </Typography>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Patient ID:
                </Typography>
                <Typography variant="body1">{patientData.pid || 'N/A'}</Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Name:
                </Typography>
                <Typography variant="body1" textAlign="right">
                  {`${patientData.first_name || ''} ${
                    patientData.last_name || ''
                  }`.trim() || 'N/A'}
                </Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Date of Birth:
                </Typography>
                <Typography variant="body1">{formatDob(patientData.dob)}</Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Gender:
                </Typography>
                <Typography variant="body1">{patientData.gender || 'N/A'}</Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Contact Number:
                </Typography>
                <Typography variant="body1">{patientData.phone || 'N/A'}</Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Address:
                </Typography>
                <Typography variant="body1" textAlign="right">
                  {[patientData.street, patientData.city, patientData.state, patientData.zip]
                    .filter(Boolean)
                    .join(', ') || 'N/A'}
                </Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Country:
                </Typography>
                <Typography variant="body1">
                  {patientData.country || 'N/A'}
                </Typography>
              </Stack>
              <Divider />

              <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    onClose();
                    dispatch(UpdateSidebarType('CONTACT'));
                  }}
                >
                  Cancel
                </Button>

                <Button variant="contained" onClick={() => setShowPatientSearch(true)}>
                  Change Patient
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No patient record found for this chat.
              </Typography>

              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 3, width: '100%' }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    onClose();
                    dispatch(UpdateSidebarType('CONTACT'));
                  }}
                >
                  Cancel
                </Button>

                <Button variant="contained" onClick={() => setShowPatientSearch(true)}>
                  Add Patient
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PatientInformation;